import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { AlertTriangle, CalendarClock, CheckCircle2, Loader2, Send, UserRound } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { sendPublicBookingAcknowledgment } from "@/lib/apiClient";
import { buildTimeChipSlots } from "@/components/calendar/TimeChipGrid";
import TimeSelect from "@/components/calendar/TimeSelect";

const EMPTY_PROFILE = {
  full_name: "",
  phone: "",
  email: "",
  date_of_birth: "",
  address: "",
};

const EMPTY_REQUEST = {
  branch_id: "",
  preferred_date: "",
  preferred_time: "",
  preferred_anytime: false,
  reason: "",
  note: "",
};

const ACKNOWLEDGEMENT_WARNING = "The clinic received your request, but the acknowledgement email could not be sent.";

export default function PublicPatientOnboarding() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [clinic, setClinic] = useState(null);
  const [error, setError] = useState(null);
  const [step, setStep] = useState("register");
  const [profile, setProfile] = useState(EMPTY_PROFILE);
  const [request, setRequest] = useState(EMPTY_REQUEST);
  const [honeypot, setHoneypot] = useState("");
  const [registered, setRegistered] = useState(null);
  const [emailWarning, setEmailWarning] = useState(null);
  const [timeSlots, setTimeSlots] = useState(buildTimeChipSlots());
  const [loadingSlots, setLoadingSlots] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    supabase.rpc("get_registration_clinic", { p_token: token })
      .then(({ data, error: loadError }) => {
        if (cancelled) return;
        setLoading(false);
        if (loadError) {
          setError(loadError.message || "This registration link is invalid.");
          return;
        }
        if (!data?.enabled) {
          setError("Online registration is currently closed. Please contact the clinic.");
          return;
        }
        const branches = Array.isArray(data?.branches) ? data.branches : [];
        setClinic({ ...data, branches });
        if (branches.length === 1) {
          setRequest((current) => ({ ...current, branch_id: branches[0].id || "" }));
        }
      });
    return () => { cancelled = true; };
  }, [token]);

  const showBranchPicker = (clinic?.branches || []).length > 1;

  const canRegister = profile.full_name.trim() && profile.phone.trim();
  const canRequest = request.preferred_anytime
    ? request.reason.trim()
    : request.preferred_date && request.preferred_time && request.reason.trim();

  useEffect(() => {
    if (step !== "booking" || request.preferred_anytime || !request.preferred_date) {
      setTimeSlots(buildTimeChipSlots());
      return;
    }
    let cancelled = false;
    setLoadingSlots(true);
    setTimeSlots([]);
    supabase.rpc("list_public_booking_time_slots", {
      p_token: token,
      p_branch_id: request.branch_id || null,
      p_date: request.preferred_date,
    }).then(({ data, error: slotsError }) => {
      if (cancelled) return;
      setLoadingSlots(false);
      if (slotsError || !Array.isArray(data) || data.length === 0) {
        setTimeSlots([]);
        return;
      }
      const nextSlots = data.map((slot) => ({
        slot_time: String(slot.slot_time || "").slice(0, 5),
        available: slot.available !== false,
        unavailable_reason: slot.unavailable_reason || null,
      })).filter((slot) => slot.slot_time);
      setTimeSlots(nextSlots);
      setRequest((current) => {
        if (
          current.preferred_time
          && nextSlots.some((slot) => slot.slot_time === current.preferred_time && slot.available === false)
        ) {
          return { ...current, preferred_time: "" };
        }
        return current;
      });
    });
    return () => { cancelled = true; };
  }, [step, token, request.branch_id, request.preferred_date, request.preferred_anytime]);

  async function submitRegistration(event) {
    event.preventDefault();
    if (!canRegister || submitting) return;
    setSubmitting(true);
    setError(null);
    const payload = {
      full_name: profile.full_name.trim(),
      phone: profile.phone.trim(),
      email: profile.email.trim() || null,
      date_of_birth: profile.date_of_birth || null,
      address: profile.address.trim() || null,
    };
    const { data, error: submitError } = await supabase.rpc("register_patient_via_token", {
      p_token: token,
      p_profile: payload,
      p_honeypot: honeypot,
    });
    setSubmitting(false);
    if (submitError) {
      setError(submitError.message || "Could not complete registration.");
      return;
    }
    setRegistered(data || {});
    setStep("booking");
  }

  async function submitBookingRequest(event) {
    event.preventDefault();
    if (!registered?.patient_id || !canRequest || submitting) return;
    setSubmitting(true);
    setError(null);
    setEmailWarning(null);
    const requestPayload = {
      branch_id: request.branch_id || null,
      preferred_date: request.preferred_anytime ? null : request.preferred_date,
      preferred_time: request.preferred_anytime ? null : request.preferred_time,
      preferred_anytime: Boolean(request.preferred_anytime),
      reason: request.reason.trim(),
      note: request.note.trim() || null,
    };
    const { data, error: requestError } = await supabase.rpc("create_patient_booking_request_via_token", {
      p_token: token,
      p_patient_id: registered.patient_id,
      p_request: requestPayload,
      p_honeypot: honeypot,
    });
    if (requestError) {
      setSubmitting(false);
      setError(requestError.message || "Could not send your request.");
      return;
    }
    if (data?.request_id && data?.acknowledgement_email_token) {
      const emailResult = await sendPublicBookingAcknowledgment({
        request_id: data.request_id,
        acknowledgement_email_token: data.acknowledgement_email_token,
      });
      if (emailResult && emailResult.ok === false) {
        setEmailWarning(true);
      }
    }
    setSubmitting(false);
    setStep("done");
  }

  return (
    <main className="min-h-screen bg-slate-50 px-3 py-4 text-slate-900 sm:px-4 sm:py-8">
      <div className="mx-auto max-w-md">
        <ClinicHeader clinic={clinic} />

        {loading ? (
          <Card>
            <div className="flex items-center justify-center gap-2 py-10 text-sm text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading...
            </div>
          </Card>
        ) : error && !clinic ? (
          <MessageCard tone="error" title="We couldn't open this link." message={error} />
        ) : step === "done" ? (
          <Card>
            <div className="py-9 text-center" data-testid="public-booking-done">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50 text-emerald-700">
                <CheckCircle2 className="h-6 w-6" />
              </div>
              <h1 className="mt-4 text-xl font-bold text-slate-950">Request received</h1>
              <p className="mt-2 text-sm leading-6 text-slate-600">
                The clinic will review your details and contact you to confirm the real appointment time.
                This appointment is not yet confirmed.
              </p>
              {emailWarning ? (
                <p className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                  {ACKNOWLEDGEMENT_WARNING}
                </p>
              ) : null}
            </div>
          </Card>
        ) : (
          <div className="space-y-3">
            <Progress step={step} />
            {error ? <InlineError message={error} /> : null}
            <Card>
              {step === "register" ? (
                <RegistrationForm
                  profile={profile}
                  setProfile={setProfile}
                  request={request}
                  setRequest={setRequest}
                  branches={clinic?.branches || []}
                  showBranchPicker={showBranchPicker}
                  honeypot={honeypot}
                  setHoneypot={setHoneypot}
                  submitting={submitting}
                  canSubmit={canRegister}
                  onSubmit={submitRegistration}
                />
              ) : (
                <BookingForm
                  patientFirstName={registered?.patient_first_name}
                  request={request}
                  setRequest={setRequest}
                  timeSlots={timeSlots}
                  loadingSlots={loadingSlots}
                  branches={clinic?.branches || []}
                  showBranchPicker={showBranchPicker}
                  honeypot={honeypot}
                  setHoneypot={setHoneypot}
                  submitting={submitting}
                  canSubmit={canRequest}
                  onSubmit={submitBookingRequest}
                />
              )}
            </Card>
          </div>
        )}
      </div>
    </main>
  );
}

function ClinicHeader({ clinic }) {
  return (
    <header className="mb-3 flex items-center justify-center gap-2">
      {clinic?.clinic_logo_url ? (
        <img src={clinic.clinic_logo_url} alt="" className="h-8 w-8 rounded-lg object-cover" />
      ) : (
        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-teal-700 text-sm font-black text-white">D</span>
      )}
      <span className="min-w-0 truncate text-sm font-bold">{clinic?.clinic_name || "Dental OS"}</span>
    </header>
  );
}

function Progress({ step }) {
  return (
    <div className="grid grid-cols-2 gap-2 text-xs font-bold">
      <div className={`rounded-lg border px-3 py-2 ${step === "register" ? "border-teal-200 bg-teal-50 text-teal-900" : "border-emerald-200 bg-emerald-50 text-emerald-800"}`}>
        <UserRound className="mr-1 inline h-3.5 w-3.5" />
        Register
      </div>
      <div className={`rounded-lg border px-3 py-2 ${step === "booking" ? "border-teal-200 bg-teal-50 text-teal-900" : "border-slate-200 bg-white text-slate-500"}`}>
        <CalendarClock className="mr-1 inline h-3.5 w-3.5" />
        Request
      </div>
    </div>
  );
}

function RegistrationForm({
  profile,
  setProfile,
  request,
  setRequest,
  branches,
  showBranchPicker,
  honeypot,
  setHoneypot,
  submitting,
  canSubmit,
  onSubmit,
}) {
  return (
    <form onSubmit={onSubmit} className="space-y-3" data-testid="public-register-form">
      <div>
        <h1 className="text-xl font-bold text-slate-950">Patient registration</h1>
        <p className="mt-1 text-sm leading-6 text-slate-600">Create your intake record first. The clinic will review and verify it before your visit.</p>
      </div>
      <TextField label="Full name" testId="public-register-name" value={profile.full_name} onChange={(value) => setProfile({ ...profile, full_name: value })} required />
      <TextField label="Mobile number" testId="public-register-phone" value={profile.phone} onChange={(value) => setProfile({ ...profile, phone: value })} required />
      <TextField label="Email" testId="public-register-email" value={profile.email} onChange={(value) => setProfile({ ...profile, email: value })} type="email" />
      <TextField label="Date of birth" testId="public-register-dob" value={profile.date_of_birth} onChange={(value) => setProfile({ ...profile, date_of_birth: value })} type="date" />
      <TextField label="Address" testId="public-register-address" value={profile.address} onChange={(value) => setProfile({ ...profile, address: value })} />
      {showBranchPicker ? (
        <BranchPicker
          branches={branches}
          value={request.branch_id}
          onChange={(value) => setRequest({ ...request, branch_id: value })}
        />
      ) : null}
      <input
        tabIndex={-1}
        autoComplete="off"
        value={honeypot}
        onChange={(event) => setHoneypot(event.target.value)}
        className="hidden"
        data-testid="public-onboarding-website"
        aria-hidden="true"
      />
      <button
        type="submit"
        disabled={!canSubmit || submitting}
        data-testid="public-register-submit"
        className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-teal-600 px-4 py-3 text-sm font-bold text-white hover:bg-teal-700 disabled:opacity-50"
      >
        {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        Continue
      </button>
    </form>
  );
}

function BookingForm({
  patientFirstName,
  request,
  setRequest,
  timeSlots,
  loadingSlots,
  branches,
  showBranchPicker,
  honeypot,
  setHoneypot,
  submitting,
  canSubmit,
  onSubmit,
}) {
  const branchPicker = showBranchPicker ? (
    <BranchPicker branches={branches} value={request.branch_id} onChange={(value) => setRequest((current) => ({ ...current, branch_id: value, preferred_time: "" }))} />
  ) : null;

  return (
    <form onSubmit={onSubmit} className="space-y-3" data-testid="public-booking-form">
      <div>
        <h1 className="text-xl font-bold text-slate-950">Request an appointment</h1>
        <p className="mt-1 text-sm leading-6 text-slate-600">
          {patientFirstName ? `${patientFirstName}, t` : "T"}ell the clinic what time works for you. Staff will contact you before anything is booked.
        </p>
      </div>
      {branchPicker}
      <label className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-700">
        <input
          type="checkbox"
          checked={request.preferred_anytime}
          onChange={(event) => setRequest((current) => ({ ...current, preferred_anytime: event.target.checked }))}
          data-testid="public-booking-anytime"
          className="h-4 w-4 rounded border-slate-300 text-teal-600"
        />
        Any available time
      </label>
      {!request.preferred_anytime ? (
        <>
          <TextField label="Preferred date" testId="public-booking-date" value={request.preferred_date} onChange={(value) => setRequest((current) => ({ ...current, preferred_date: value, preferred_time: "" }))} type="date" required />
          <div>
            <div className="mb-1 flex items-center justify-between gap-2">
              <span className="block text-[10px] font-bold uppercase tracking-wide text-slate-500">Preferred time *</span>
              {loadingSlots ? (
                <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-slate-400">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Checking
                </span>
              ) : null}
            </div>
            {request.preferred_date ? (
              <TimeSelect
                value={request.preferred_time}
                onChange={(preferred_time) => setRequest((current) => ({ ...current, preferred_time }))}
                slots={timeSlots}
                testId="public-booking-time"
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-100 disabled:bg-slate-50 disabled:text-slate-400"
                placeholder="Pick a time"
              />
            ) : (
              <p className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-3 py-3 text-xs text-slate-500">
                Pick a date to see available times.
              </p>
            )}
          </div>
        </>
      ) : null}
      <TextField label="Reason for visit" testId="public-booking-reason" value={request.reason} onChange={(value) => setRequest((current) => ({ ...current, reason: value }))} required />
      <TextArea label="Note" testId="public-booking-note" value={request.note} onChange={(value) => setRequest((current) => ({ ...current, note: value }))} />
      <input
        tabIndex={-1}
        autoComplete="off"
        value={honeypot}
        onChange={(event) => setHoneypot(event.target.value)}
        className="hidden"
        aria-hidden="true"
      />
      <button
        type="submit"
        disabled={!canSubmit || submitting}
        data-testid="public-booking-submit"
        className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-teal-600 px-4 py-3 text-sm font-bold text-white hover:bg-teal-700 disabled:opacity-50"
      >
        {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        Send request
      </button>
      <p className="text-center text-xs leading-5 text-slate-500">This is not a confirmed appointment.</p>
    </form>
  );
}

function BranchPicker({ branches, value, onChange }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[10px] font-bold uppercase tracking-wide text-slate-500">Preferred branch</span>
      <select
        value={value || ""}
        onChange={(event) => onChange(event.target.value)}
        data-testid="public-booking-branch"
        className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-100"
      >
        <option value="">No preference</option>
        {branches.map((branch) => (
          <option key={branch.id} value={branch.id}>{branch.name}</option>
        ))}
      </select>
    </label>
  );
}

function TextField({ label, testId, value, onChange, type = "text", required = false }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[10px] font-bold uppercase tracking-wide text-slate-500">{label}{required ? " *" : ""}</span>
      <input
        type={type}
        value={value || ""}
        onChange={(event) => onChange(event.target.value)}
        data-testid={testId}
        className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-100"
      />
    </label>
  );
}

function TextArea({ label, testId, value, onChange }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[10px] font-bold uppercase tracking-wide text-slate-500">{label}</span>
      <textarea
        value={value || ""}
        onChange={(event) => onChange(event.target.value)}
        data-testid={testId}
        rows={4}
        className="w-full resize-none rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-100"
      />
    </label>
  );
}

function Card({ children }) {
  return <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">{children}</section>;
}

function InlineError({ message }) {
  return (
    <div className="flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800" role="alert">
      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
      <span>{message}</span>
    </div>
  );
}

function MessageCard({ title, message }) {
  return (
    <Card>
      <div className="py-8 text-center">
        <AlertTriangle className="mx-auto h-8 w-8 text-rose-500" />
        <h1 className="mt-3 text-lg font-bold text-slate-950">{title}</h1>
        <p className="mt-2 text-sm leading-6 text-slate-600">{message}</p>
      </div>
    </Card>
  );
}
